import { useEffect, useState } from "react";
import { Target } from "@phosphor-icons/react";
import { EmptyState } from "../components/shared/EmptyState";
import { BestBetsSummary } from "../components/best-bets/BestBetsSummary";
import { BestBetsTable } from "../components/best-bets/BestBetsTable";
import { ProbabilityBuckets } from "../components/best-bets/ProbabilityBuckets";
import { getBestBets } from "../lib/api";

const BestBets = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBestBets()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="best-bets-page" className="mx-auto flex max-w-[1440px] flex-col gap-8 px-4 py-8 sm:px-6">
      <div>
        <h1 className="font-heading text-3xl uppercase tracking-tight text-white">Largest Leans</h1>
        <p data-testid="best-bets-description" className="mt-1 font-mono text-xs text-zinc-500">
          Decision support, not automatic betting advice -- the model&rsquo;s strongest leans at the recorded line.
        </p>
        <p data-testid="best-bets-confidence-disclaimer" className="mt-1 font-mono text-xs text-zinc-500">
          Model confidence is a directional estimate, not a guaranteed win probability.
        </p>
      </div>

      {loading ? (
        <p data-testid="best-bets-loading" className="font-mono text-xs text-zinc-500">Loading Largest Leans...</p>
      ) : !data ? (
        <EmptyState icon={Target} title="Largest Leans Unavailable" description="Could not load the Largest Leans tracker." testId="best-bets-empty" />
      ) : (
        <>
          <div data-testid="best-bets-forward-section" className="flex flex-col gap-4">
            <div>
              <h2 className="font-heading text-lg uppercase tracking-tight text-white">Forward-Verified Performance</h2>
              <p className="font-mono text-[10px] text-zinc-500">
                Only leans captured under the strict forward-only capture gate, counted from the moment they qualified.
                Excludes historical/backfilled and legacy records entirely -- see Historical / Legacy below for those.
              </p>
            </div>
            <BestBetsSummary summary={data.forward_summary} sectionId="forward" showMinSampleNote />
            <ProbabilityBuckets buckets={data.forward_buckets} sectionId="forward-buckets" />
          </div>

          <div data-testid="best-bets-legacy-section" className="flex flex-col gap-4">
            <div>
              <h2 className="font-heading text-lg uppercase tracking-tight text-white">Historical / Legacy Performance</h2>
              <p className="font-mono text-[10px] text-zinc-500">
                Backfilled and pre-verification records, kept fully separate from the forward-verified performance above
                -- never blended into one combined number.
              </p>
            </div>
            <BestBetsSummary summary={data.legacy_summary} sectionId="legacy" />
            <ProbabilityBuckets buckets={data.legacy_buckets} sectionId="legacy-buckets" />
          </div>

          {data.picks.length === 0 ? (
            <EmptyState icon={Target} title="No Largest Leans Yet" description="No Full Game, First 5, NRFI or YRFI pick has crossed the qualification threshold yet." testId="best-bets-no-picks" />
          ) : (
            <BestBetsTable picks={data.picks} />
          )}
        </>
      )}
    </div>
  );
};

export default BestBets;
